"use client";

import { useAPI } from "@/lib/api";
import React, {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface AppContextInterface {
  kind: "HOME" | "BLOG" | "MICROSITE" | "ADMIN" | "RESELLER" | "SHOP";
  host?: string;
  locale?: string;
  alternate?: string;
  captchaVendor?: "TURNSTILE" | "HCAPTCHA" | "RECAPTCHA" | "RANDOM" | "UNKNOWN";
}

const useAppContextHook = ({
  kind,
  alternate,
  locale,
  host,
  captchaVendor = "RANDOM",
}: AppContextInterface) => {
  const [showAds, setShowAds] = useState(true);
  const [cVendor, setCaptchaVendor] = useState(captchaVendor);
  const refreshTokenRef = useRef<Promise<void> | null>(null);
  return {
    kind,
    showAds,
    alternate,
    setShowAds,
    locale,
    host,
    captchaVendor: cVendor,
    setCaptchaVendor,
    refreshTokenRef,
  };
};

const AppContext = createContext<ReturnType<typeof useAppContextHook>>(
  {} as any
);

type AppContextProviderProps = {
  value: AppContextInterface;
  children: React.ReactNode;
};

const AppContextProvider = ({ children, value }: AppContextProviderProps) => {
  const _value = useAppContextHook({
    kind: value.kind,
    alternate: value.alternate,
    locale: value.locale,
    host: value.host,
  });
  return (
    <AppContext.Provider value={_value}>
      <CaptchaProvider>{children}</CaptchaProvider>
    </AppContext.Provider>
  );
};

const CaptchaProvider = ({ children }) => {
  const { setCaptchaVendor } = useAppContext();
  const api = useAPI();
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/captcha-vendor");
        setCaptchaVendor(data);
      } catch {
        //...
      }
    })();
    return () => {};
  }, []);

  return <Fragment>{children}</Fragment>;
};

export const useAppContext = () => useContext(AppContext);

export default AppContextProvider;
